class CustomDealerListV3 extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.pinnedDealer = null;
    this.pinnedDealerId = "";
    this.notFoundTitle = this.getAttribute("not-found-title") || "No Results Found.";
    this.notFoundText =
      this.getAttribute("not-found-text") || "Try enter another address or switch to a specific country website, or contact us via email.";
    this.emailText = this.getAttribute("email-text") || "Email Navimow";
    this.emailAddress = this.getAttribute("email-address") || "navimow@navimow.com";
    this.unit = this.getAttribute("unit") ? this.getAttribute("unit") : " km";
    this.visitWebsiteText =
      this.getAttribute("visit-website-text") || "Visit Web";
    this.directionsText = this.getAttribute("directions-text") || "Directions";
    this.moreInformationText =
      this.getAttribute("more-information-text") || "More Information";
    this.productSeriesText =
      this.getAttribute("product-series-text") || "Product Series";
    this.servicesText = this.getAttribute("services-text") || "Services";
    this.showProductInfo = this._getBooleanAttribute("show-product-info", true);
    this.showServiceInfo = this._getBooleanAttribute("show-service-info", true);
    this.showChannelInfo = this._getBooleanAttribute("show-channel-info", true);
    this.showLevelInfo = this._getBooleanAttribute("show-level-info", true);
    this.showBusinessHourInfo = this._getBooleanAttribute("show-business-hour-info", true);
    
    // 虚拟滚动配置
    this.ITEM_HEIGHT_ESTIMATE = 150; // 每个 item 的预估高度（px），会动态更新
    this.ITEM_GAP = 8; // item 之间的间距（px），对应 gap-y-2
    this.BUFFER_SIZE = 5; // 上下缓冲的 item 数量（增加缓冲减少跳变）
    this.visibleStart = 0;
    this.visibleEnd = 0;
    this._cachedScrollTop = 0; // 缓存的滚动位置
    this._cachedContainerHeight = 0; // 缓存的容器高度
    this.useVirtualScroll = false; // 超过阈值才启用虚拟滚动
    this.VIRTUAL_SCROLL_THRESHOLD = 50; // 超过 50 个 item 启用虚拟滚动
    this._itemHeights = []; // 缓存每个 item 的实际高度
    this._measuredCount = 0; // 已测量的 item 数量
    
    // 防抖滚动处理
    this.scrollTimer = null;
    this.isScrolling = false;
    this._appendChunkSize = 20;
    this._dealerSelectedTimer = null;
    this._fadeStateRaf = 0;
    this._resizeObserver = null;
    this.MOBILE_DETAIL_OPEN_DELAY_MS = 800;
  }

  connectedCallback() {
    if (Object.prototype.hasOwnProperty.call(this, "data")) {
      const initialData = this.data;
      delete this.data;
      this.data = initialData;
    }

    if (!this._clickDelegated) {
      this._clickDelegated = true;
      this.addEventListener("click", (e) => this._handleDelegatedClick(e));
    }

    this._observeFadeStateResize();
    this._scheduleFadeStateSync();
  }

  disconnectedCallback() {
    if (this._fadeStateRaf) {
      cancelAnimationFrame(this._fadeStateRaf);
      this._fadeStateRaf = 0;
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }
  
  set data(newData) {
    this.items = Array.isArray(newData) ? newData : [];
    this.pinnedDealer = null;
    this.pinnedDealerId = "";
    this.useVirtualScroll = this.items.length > this.VIRTUAL_SCROLL_THRESHOLD;
    // 重置高度缓存
    this._itemHeights = [];
    this._measuredCount = 0;
    this.render();
  }

  appendData(newData) {
    return this.appendDataAsync(newData, this._appendChunkSize);
  }

  appendDataAsync(newData, chunkSize = this._appendChunkSize) {
    const items = Array.isArray(newData) ? newData : [];
    if (items.length === 0) return Promise.resolve();

    this.items = this.items.concat(items);

    if (this.useVirtualScroll) {
      this.render();
      this._scheduleFadeStateSync();
      return Promise.resolve();
    }

    const container = this._getListContainer();
    if (!container) return Promise.resolve();

    return new Promise((resolve) => {
      let index = 0;

      const appendChunk = () => {
        const end = Math.min(index + chunkSize, items.length);
        const fragment = document.createDocumentFragment();
        for (; index < end; index++) {
          fragment.appendChild(this._renderNormalItem(items[index]));
        }
        container.appendChild(fragment);

        if (index < items.length) {
          requestAnimationFrame(appendChunk);
        } else {
          this._scheduleFadeStateSync();
          resolve();
        }
      };

      requestAnimationFrame(appendChunk);
    });
  }

  _getListContainer() {
    let container = this._container || this.querySelector(":scope > .dealer-list-container");
    const isEmptyState =
      container &&
      !this.querySelector(".custom-dealer-item") &&
      container.querySelector(".text-center");

    if (!container || isEmptyState) {
      this.render();
      container = this._container;
    }

    return container;
  }

  render() {
    this.innerHTML = "";
    this.ITEM_HEIGHT_ESTIMATE = this.closest(".where-to-buy-v4") ? 240 : 150;
    const container = document.createElement("div");
    this._container = container;
    container.className = "dealer-list-container";

    if (this.items.length === 0 && !this.pinnedDealer) {
      container.innerHTML = this._renderEmpty();
      this.appendChild(container);
      this._addClickHandlers();
      this._scheduleFadeStateSync();
      return;
    }

    if (this.pinnedDealer) {
      container.appendChild(this._renderPinnedItem(this.pinnedDealer));
    }

    if (this.useVirtualScroll) {
      // 虚拟滚动：使用占位符 + 只渲染可见区域
      this._renderVirtual(container);
    } else {
      // 少量数据：直接渲染全部
      this.items.forEach((item) => {
        container.appendChild(this._renderNormalItem(item));
      });
    }
    
    this.appendChild(container);
    this._addClickHandlers();
    
    // 虚拟滚动需要监听滚动事件
    if (this.useVirtualScroll) {
      this._setupVirtualScroll();
    }

    this._scheduleFadeStateSync();
  }

  _observeFadeStateResize() {
    if (this._resizeObserver || typeof ResizeObserver === "undefined") return;

    this._resizeObserver = new ResizeObserver(() => this._scheduleFadeStateSync());
    this._resizeObserver.observe(this);

    const shell = this.closest(".wtb-dealer-list-shell");
    if (shell) this._resizeObserver.observe(shell);
  }

  _scheduleFadeStateSync() {
    if (this._fadeStateRaf) cancelAnimationFrame(this._fadeStateRaf);

    this._fadeStateRaf = requestAnimationFrame(() => {
      this._fadeStateRaf = 0;
      this._syncFadeState();
    });
  }

  _syncFadeState() {
    const shell = this.closest(".wtb-dealer-list-shell");
    const hasDealers = this.items.length > 0 || Boolean(this.pinnedDealer);
    const hasScrollableOverflow = hasDealers && this.scrollHeight > this.clientHeight + 1;

    this.classList.toggle("has-scroll-fade", hasScrollableOverflow);
    if (shell) shell.classList.toggle("has-scroll-fade", hasScrollableOverflow);
  }

  setPinnedDealer(item) {
    if (!item || !item.dealerId) return null;

    const container = this._getListContainer();
    if (!container) return null;

    container.querySelectorAll(".custom-dealer-item--pinned-duplicate").forEach((el) => {
      el.classList.remove("custom-dealer-item--pinned-duplicate");
      el.hidden = false;
    });
    container.querySelector(".custom-dealer-item--pinned")?.remove();

    this.pinnedDealer = item;
    this.pinnedDealerId = String(item.dealerId);

    if (!container.querySelector(".custom-dealer-item") && container.querySelector(".text-center")) {
      container.innerHTML = "";
    }

    container.querySelectorAll(".custom-dealer-item").forEach((el) => {
      if (el.dataset.dealerid === this.pinnedDealerId) {
        el.classList.add("custom-dealer-item--pinned-duplicate");
        el.hidden = true;
      }
    });

    const pinnedItem = this._renderPinnedItem(item);
    const virtualContainer = container.querySelector(".dealer-list-visible");
    container.insertBefore(pinnedItem, virtualContainer || container.firstChild);
    this._setActiveDealer(this.pinnedDealerId);
    this.scrollTo(0);
    this._scheduleFadeStateSync();
    return pinnedItem;
  }

  _clearPinnedDealer() {
    const container = this._container || this.querySelector(":scope > .dealer-list-container");

    if (container) {
      container.querySelector(".custom-dealer-item--pinned")?.remove();
      container.querySelectorAll(".custom-dealer-item--pinned-duplicate").forEach((el) => {
        el.classList.remove("custom-dealer-item--pinned-duplicate");
        el.hidden = false;
      });
    }

    this.pinnedDealer = null;
    this.pinnedDealerId = "";
    this._scheduleFadeStateSync();
  }

  _isPinnedDealer(item) {
    return Boolean(this.pinnedDealerId && item && String(item.dealerId) === this.pinnedDealerId);
  }

  _renderPinnedItem(item) {
    const el = this._renderItem(item);
    el.classList.add("custom-dealer-item--pinned", "active");
    return el;
  }

  _renderNormalItem(item) {
    const el = this._renderItem(item);
    if (this._isPinnedDealer(item)) {
      el.classList.add("custom-dealer-item--pinned-duplicate");
      el.hidden = true;
    }
    return el;
  }

  _setActiveDealer(dealerId) {
    this.querySelectorAll(".custom-dealer-item").forEach((el) => {
      el.classList.toggle("active", el.dataset.dealerid === String(dealerId));
    });
  }
  
  _renderVirtual(container) {
    // 重置高度缓存
    this._itemHeights = [];
    this._measuredCount = 0;
    
    // 计算总高度（占位符，使用预估高度 + 间距）
    // 最后一个 item 没有间距，所以是 (n-1) * gap + n * height
    const totalHeight = this.items.length * this.ITEM_HEIGHT_ESTIMATE + 
                        (this.items.length > 0 ? (this.items.length - 1) * this.ITEM_GAP : 0);
    container.style.height = `${totalHeight}px`;
    container.style.position = 'relative';
    container.style.width = '100%';
    
    // 创建可见区域容器
    const visibleContainer = document.createElement("div");
    visibleContainer.className = "dealer-list-visible";
    visibleContainer.style.position = 'absolute';
    visibleContainer.style.top = '0';
    visibleContainer.style.left = '0';
    visibleContainer.style.right = '0';
    visibleContainer.style.width = '100%';
    container.appendChild(visibleContainer);
    
    // 初始渲染可见区域（延迟确保容器已添加到 DOM）
    // 使用 requestAnimationFrame 确保 DOM 已渲染
    requestAnimationFrame(() => {
      this._cachedScrollTop = this.scrollTop || 0;
      // 如果容器高度还没计算出来，使用默认值或父容器高度
      const height = this.clientHeight || this.parentElement?.clientHeight || 600;
      this._cachedContainerHeight = height;
      this._updateVisibleItems(visibleContainer);
      
      // 如果高度还是 0，再延迟一次
      if (this._cachedContainerHeight === 0) {
        setTimeout(() => {
          this._cachedContainerHeight = this.clientHeight || this.parentElement?.clientHeight || 600;
          this._updateVisibleItems(visibleContainer);
        }, 100);
      }
    });
  }
  
  _updateVisibleItems(visibleContainer) {
    if (!this.useVirtualScroll || this.items.length === 0) return;
    
    // 从实际 DOM 获取滚动位置和容器高度
    const scrollTop = this._cachedScrollTop || 0;
    const containerHeight = this._cachedContainerHeight || this.clientHeight || 600;
    
    // 计算总高度（用于检测是否滚动到底部）
    let totalHeight = 0;
    for (let i = 0; i < this.items.length; i++) {
      totalHeight += this._getItemHeightWithGap(i);
    }
    
    // 确保总高度足够，让最后一个 item 可以完全滚动到底部
    const lastItemHeight = this._getItemHeight(this.items.length - 1);
    const minTotalHeight = containerHeight + lastItemHeight;
    totalHeight = Math.max(totalHeight, minTotalHeight);
    
    const maxScrollTop = Math.max(0, totalHeight - containerHeight);
    const isAtBottom = scrollTop >= maxScrollTop - 5; // 接近底部（5px 容差，允许继续滚动）
    
    // 使用累积高度计算可见范围（更准确，包含间距）
    let startIndex = 0;
    // 初始 endIndex：至少显示一屏的内容 + 缓冲
    const itemsPerScreen = Math.ceil(containerHeight / (this.ITEM_HEIGHT_ESTIMATE + this.ITEM_GAP));
    let endIndex = Math.min(this.items.length - 1, itemsPerScreen + this.BUFFER_SIZE);
    let accumulatedHeight = 0;
    
    // 计算起始索引（向上查找，使用包含间距的高度）
    const bufferHeight = this.BUFFER_SIZE * (this.ITEM_HEIGHT_ESTIMATE + this.ITEM_GAP);
    for (let i = 0; i < this.items.length; i++) {
      const itemHeightWithGap = this._getItemHeightWithGap(i);
      if (accumulatedHeight + itemHeightWithGap >= scrollTop - bufferHeight) {
        startIndex = Math.max(0, i - this.BUFFER_SIZE);
        break;
      }
      accumulatedHeight += itemHeightWithGap;
    }
    
    // 计算结束索引（向下查找，从 startIndex 开始）
    accumulatedHeight = 0;
    for (let i = 0; i < startIndex; i++) {
      accumulatedHeight += this._getItemHeightWithGap(i);
    }
    
    // 计算到可见区域底部需要的高度
    const targetBottom = scrollTop + containerHeight + bufferHeight;
    let foundEndIndex = false;
    let lastItemStartHeight = 0; // 最后一个 item 的起始位置
    
    // 先计算总高度，用于检测最后一个 item 是否可见
    let totalAccumulatedHeight = 0;
    for (let i = 0; i < this.items.length; i++) {
      if (i === this.items.length - 1) {
        lastItemStartHeight = totalAccumulatedHeight;
      }
      totalAccumulatedHeight += this._getItemHeightWithGap(i);
    }
    
    // 检查最后一个 item 是否部分可见
    const lastItemEndHeight = totalAccumulatedHeight;
    const lastItemVisible = lastItemStartHeight < scrollTop + containerHeight && lastItemEndHeight > scrollTop;
    
    for (let i = startIndex; i < this.items.length; i++) {
      const itemHeightWithGap = this._getItemHeightWithGap(i);
      accumulatedHeight += itemHeightWithGap;
      
      // 如果累积高度超过目标底部，设置 endIndex
      if (accumulatedHeight >= targetBottom) {
        endIndex = Math.min(this.items.length - 1, i + this.BUFFER_SIZE);
        foundEndIndex = true;
        break;
      }
    }
    
    // 如果最后一个 item 可见（即使是部分可见），确保 endIndex 包含它
    if (lastItemVisible) {
      endIndex = this.items.length - 1;
      foundEndIndex = true;
    }
    
    // 如果循环结束还没找到（滚动到底部），确保 endIndex 包含最后一个 item
    if (!foundEndIndex) {
      endIndex = this.items.length - 1;
    }
    
    // 如果滚动到底部，确保 endIndex 包含最后一个 item
    if (isAtBottom) {
      endIndex = this.items.length - 1;
      // 同时确保 startIndex 不会太靠前，保持合理的可见范围
      startIndex = Math.max(0, Math.min(startIndex, this.items.length - itemsPerScreen - this.BUFFER_SIZE));
    }
    
    // 确保 endIndex 至少包含可见区域，且不超过数组范围
    if (endIndex < startIndex) {
      endIndex = Math.min(this.items.length - 1, startIndex + itemsPerScreen + this.BUFFER_SIZE);
    }
    
    // 最终确保 endIndex 不超过数组范围，且至少等于 startIndex
    endIndex = Math.max(startIndex, Math.min(this.items.length - 1, endIndex));
    
    // 计算可见区域的 top 位置（累积高度，包含间距）
    let topOffset = 0;
    for (let i = 0; i < startIndex; i++) {
      topOffset += this._getItemHeightWithGap(i);
    }
    
    // 如果范围没变化，只更新 top 位置（可能因为高度测量导致偏移）
    if (startIndex === this.visibleStart && endIndex === this.visibleEnd && visibleContainer.children.length > 0) {
      const currentTop = parseInt(visibleContainer.style.top) || 0;
      if (Math.abs(currentTop - topOffset) > 1) {
        // 高度有微小变化，只调整位置
        visibleContainer.style.top = `${topOffset}px`;
      }
      return;
    }
    
    // 智能更新：只更新变化的 item，减少 DOM 操作
    const oldStart = this.visibleStart;
    const oldEnd = this.visibleEnd;
    const children = visibleContainer.children;
    const isInitialRender = children.length === 0;
    
    // 如果是初始渲染或范围变化很大，直接重新渲染（更可靠）
    if (isInitialRender || 
        Math.abs(startIndex - oldStart) > this.BUFFER_SIZE * 2 || 
        Math.abs(endIndex - oldEnd) > this.BUFFER_SIZE * 2) {
      const fullFragment = document.createDocumentFragment();
      for (let i = startIndex; i <= endIndex; i++) {
        const item = this.items[i];
        if (item) {
          const itemEl = this._renderNormalItem(item);
          fullFragment.appendChild(itemEl);
        }
      }
      visibleContainer.innerHTML = '';
      visibleContainer.style.top = `${topOffset}px`;
      visibleContainer.appendChild(fullFragment);
    } else if (startIndex !== oldStart || endIndex !== oldEnd) {
      // 范围扩大或缩小，需要更新
      // 计算需要保留的 item（重叠部分）
      const keepStart = Math.max(startIndex, oldStart);
      const keepEnd = Math.min(endIndex, oldEnd);
      
      // 移除不在新范围内的 item（从后往前移除，避免索引变化）
      const itemsToRemove = [];
      for (let i = children.length - 1; i >= 0; i--) {
        const itemIndex = oldStart + i;
        if (itemIndex < startIndex || itemIndex > endIndex) {
          itemsToRemove.push(children[i]);
        }
      }
      itemsToRemove.forEach(el => el.remove());
      
      // 添加新范围内的 item
      const fragment = document.createDocumentFragment();
      
      // 在前面添加新的 item
      for (let i = startIndex; i < keepStart; i++) {
        const item = this.items[i];
        if (item) {
          const itemEl = this._renderNormalItem(item);
          fragment.appendChild(itemEl);
        }
      }
      if (fragment.children.length > 0) {
        visibleContainer.insertBefore(fragment, visibleContainer.firstChild);
      }
      
      // 在后面添加新的 item
      const fragmentEnd = document.createDocumentFragment();
      for (let i = keepEnd + 1; i <= endIndex; i++) {
        const item = this.items[i];
        if (item) {
          const itemEl = this._renderNormalItem(item);
          fragmentEnd.appendChild(itemEl);
        }
      }
      if (fragmentEnd.children.length > 0) {
        visibleContainer.appendChild(fragmentEnd);
      }
      
      // 更新 top 位置
      visibleContainer.style.top = `${topOffset}px`;
    } else {
      // 范围没变，只更新位置（可能因为高度测量导致）
      visibleContainer.style.top = `${topOffset}px`;
    }
    
    this.visibleStart = startIndex;
    this.visibleEnd = endIndex;
    
    // 测量新渲染的 item 高度并更新缓存
    requestAnimationFrame(() => {
      this._measureItemHeights(visibleContainer, startIndex);
    });
    
    // 重新绑定事件（因为 DOM 更新了）
    this._addClickHandlers();
  }
  
  _getItemHeight(index) {
    // 如果已测量过，使用实际高度；否则使用预估高度
    // 注意：返回的高度不包括间距，间距在计算总高度时单独处理
    if (this._itemHeights[index] !== undefined) {
      return this._itemHeights[index];
    }
    return this.ITEM_HEIGHT_ESTIMATE;
  }
  
  _getItemHeightWithGap(index) {
    // 返回 item 高度 + 间距（最后一个 item 没有间距）
    const height = this._getItemHeight(index);
    if (index < this.items.length - 1) {
      return height + this.ITEM_GAP;
    }
    return height;
  }
  
  _measureItemHeights(visibleContainer, startIndex) {
    // 测量可见 item 的实际高度并更新缓存
    const children = visibleContainer.children;
    let hasHeightChange = false;
    
    for (let i = 0; i < children.length; i++) {
      const itemIndex = startIndex + i;
      if (itemIndex < this.items.length) {
        const itemEl = children[i];
        const height = itemEl.offsetHeight || itemEl.getBoundingClientRect().height;
        if (height > 0) {
          const oldHeight = this._itemHeights[itemIndex];
          this._itemHeights[itemIndex] = height;
          
          // 如果高度有变化，标记需要更新总高度
          if (oldHeight === undefined || Math.abs(oldHeight - height) > 1) {
            hasHeightChange = true;
          }
          
          this._measuredCount++;
          
          // 如果测量了足够多的 item，更新平均高度作为新的预估高度
          if (this._measuredCount >= 10 && this._measuredCount % 5 === 0) {
            const measuredHeights = this._itemHeights.filter(h => h !== undefined);
            if (measuredHeights.length > 0) {
              const avgHeight = measuredHeights.reduce((a, b) => a + b, 0) / measuredHeights.length;
              this.ITEM_HEIGHT_ESTIMATE = Math.round(avgHeight);
            }
          }
        }
      }
    }
    
    // 如果高度有变化，更新总高度并重新计算可见区域
    if (hasHeightChange) {
      this._updateTotalHeight();
      // 重新计算可见区域，确保最后一个 item 完全可见
      requestAnimationFrame(() => {
        this._cachedScrollTop = Math.max(0, this.scrollTop || 0);
        this._updateVisibleItems(visibleContainer);
      });
    }
  }
  
  _updateTotalHeight() {
    const container = this.querySelector('.dealer-list-container');
    if (!container) return;
    
    // 计算总高度：已测量的用实际高度，未测量的用预估高度，包含间距
    let totalHeight = 0;
    for (let i = 0; i < this.items.length; i++) {
      totalHeight += this._getItemHeightWithGap(i);
    }
    
    // 确保总高度足够，让最后一个 item 可以完全滚动到底部
    // 总高度应该至少等于：容器高度 + 最后一个 item 的高度
    const containerHeight = this.clientHeight || 600;
    const lastItemHeight = this._getItemHeight(this.items.length - 1);
    const minTotalHeight = containerHeight + lastItemHeight;
    totalHeight = Math.max(totalHeight, minTotalHeight);
    
    container.style.height = `${totalHeight}px`;
  }
  
  _setupVirtualScroll() {
    // 移除旧的监听器
    if (this._onScroll) {
      this.removeEventListener('scroll', this._onScroll);
    }
    
    // 添加滚动监听（使用 passive 提升性能）
    this._onScroll = () => {
      if (this.scrollTimer) {
        cancelAnimationFrame(this.scrollTimer);
      }
      
      this.scrollTimer = requestAnimationFrame(() => {
        const container = this.querySelector('.dealer-list-container');
        const visibleContainer = this.querySelector('.dealer-list-visible');
        
        if (container && visibleContainer) {
          // 从实际 DOM 获取滚动位置（HTMLElement.scrollTop 属性）
          // 使用 Math.max 确保 scrollTop 不为负数
          this._cachedScrollTop = Math.max(0, this.scrollTop || 0);
          this._cachedContainerHeight = this.clientHeight || 600;
          this._updateVisibleItems(visibleContainer);
        }
      });
    };
    
    // 使用 passive 监听器提升滚动性能
    this.addEventListener('scroll', this._onScroll, { passive: true });
    
    // 初始化时计算一次（延迟确保 DOM 已渲染）
    setTimeout(() => {
      this._cachedScrollTop = this.scrollTop || 0;
      this._cachedContainerHeight = this.clientHeight || 600;
      const visibleContainer = this.querySelector('.dealer-list-visible');
      if (visibleContainer) {
        this._updateVisibleItems(visibleContainer);
      }
    }, 0);
    
    // 监听 resize 更新容器高度
    const resizeObserver = new ResizeObserver(() => {
      this._cachedContainerHeight = this.clientHeight || 600;
      const visibleContainer = this.querySelector('.dealer-list-visible');
      if (visibleContainer) {
        this._updateVisibleItems(visibleContainer);
      }
    });
    resizeObserver.observe(this);
  }
  
  scrollTo(top) {
    // 调用原生 scrollTo
    if (typeof super.scrollTo === 'function') {
      super.scrollTo({ top, behavior: 'auto' });
    } else {
      this.scrollTop = top;
    }
    
    if (this.useVirtualScroll) {
      this._cachedScrollTop = top;
      const visibleContainer = this.querySelector('.dealer-list-visible');
      if (visibleContainer) {
        // 延迟更新，确保 scrollTo 完成
        requestAnimationFrame(() => {
          this._updateVisibleItems(visibleContainer);
        });
      }
    }
  }

  _renderEmpty() {
    return `
      <div class="text-center text-base px-2 py-5">
        <div class="flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="29" height="39" viewBox="0 0 29 39" fill="none">
            <path d="M3.6 14.145C3.6 8.3775 8.3775 3.6 14.4 3.6C20.4225 3.6 25.2 8.3775 25.2 14.145C25.2 17.565 23.415 21.7575 20.7825 25.9275C18.66 29.2875 16.2075 32.2875 14.4 34.335C12.5925 32.2875 10.1475 29.295 8.0175 25.9275C5.385 21.765 3.6 17.565 3.6 14.145ZM14.4 0C6.45 0 0 6.33 0 14.145C0 23.0925 9.015 33.8175 12.78 37.905C13.665 38.865 15.135 38.865 16.02 37.905C19.785 33.8175 28.8 23.0925 28.8 14.145C28.8 6.33 22.35 0 14.4 0ZM19.4925 9.3075C18.7875 8.6025 17.6475 8.6025 16.95 9.3075L14.4075 11.85L11.865 9.3075C11.16 8.6025 10.02 8.6025 9.3225 9.3075C8.625 10.0125 8.6175 11.1525 9.3225 11.85L11.865 14.3925L9.3225 16.935C8.6175 17.64 8.6175 18.78 9.3225 19.4775C10.0275 20.175 11.1675 20.1825 11.865 19.4775L14.4075 16.935L16.95 19.4775C17.655 20.1825 18.795 20.1825 19.4925 19.4775C20.19 18.7725 20.1975 17.6325 19.4925 16.935L16.95 14.3925L19.4925 11.85C20.1975 11.145 20.1975 10.005 19.4925 9.3075Z" fill="black" fill-opacity="0.24"/>
          </svg>
        </div>
        <div class="font-[600] text-[20px] leading-[125%] tracking-[-0.4px] text-[rgba(0, 0, 0, 0.88)] mb-3">${this.notFoundTitle}</div>
        <div class="font-[400] leading-[125%] text-[14px] tracking-[-0.14px] text-[rgba(0, 0, 0, 0.66)]">${this.notFoundText}</div>
        
        <a href="mailto:${this.emailAddress}" class="flex items-center justify-center gap-2 mt-6 px-4 py-[10px] rounded-[4px] bg-[rgba(0,0,0,0.04)] w-fit mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2.79961 3.19995C2.13711 3.19995 1.59961 3.73745 1.59961 4.39995C1.59961 4.77745 1.77711 5.13245 2.07961 5.35995L7.27961 9.25995C7.70711 9.57995 8.29211 9.57995 8.71961 9.25995L13.9196 5.35995C14.2221 5.13245 14.3996 4.77745 14.3996 4.39995C14.3996 3.73745 13.8621 3.19995 13.1996 3.19995H2.79961ZM1.59961 6.49995V11.2C1.59961 12.0825 2.31711 12.8 3.19961 12.8H12.7996C13.6821 12.8 14.3996 12.0825 14.3996 11.2V6.49995L9.43961 10.22C8.58711 10.86 7.41211 10.86 6.55961 10.22L1.59961 6.49995Z" fill="black" fill-opacity="0.88"/>
          </svg>
          <span class="text-[16px] leading-[125%] tracking-[-0.16px] font-[600] text-[rgba(0, 0, 0, 0.88)]">${this.emailText}</span>
        </a>
      </div>`;
  }

  _renderItem(item) {
    const el = document.createElement("div");
    el.classList.add("custom-dealer-item");
    el._dealerData = item;
    el.dataset.lat = item.latitude;
    el.dataset.long = item.longitude;
    el.dataset.name = item.name;
    el.dataset.dealerid = item.dealerId;
    el.dataset.distanceinmeters = item.distanceInMeters;
    const isWhereToBuyV4 = this.closest(".where-to-buy-v4");
    if (!isWhereToBuyV4) {
      el.dataset.cornermark = item.cornerMark;
    }

    if (!isWhereToBuyV4) {
      el.innerHTML = `
        <div class="custom-dealer-item-title flex justify-between items-center gap-4">
          <div class="leading-[125%] tracking-[-0.16px] font-[600] text-[16px]">${this._escapeHTML(item.name)}</div>
          ${item.distanceInMeters != null ? this._renderDistance(item.distanceInMeters) : ""}
        </div>
        <div class="flex flex-col gap-y-[12px] [&_svg]:mt-[1.2px]">
          ${this._renderTags(item.productFilter)}
          ${item.address && item.address !== "null" ? this._renderAddress(item) : ""}
          ${item.phone && item.phone !== "null" ? this._renderPhone(item.phone, item.name, item.dealerId) : ""}
          ${item.email && item.email !== "null" ? this._renderEmail(item.email, item.name, item.dealerId) : ""}
          ${item.website && item.website !== "null" ? this._renderWebsite(item.website, item.name, item.dealerId) : ""}
        </div>
        ${item.cornerMark && item.cornerMark !== "null" ? this._renderCornerMark(item.cornerMark) : ""}
      `;
      return el;
    }

    const name = this._escapeHTML(item.name);
    const channelLevel = this.showLevelInfo ? this._getFirstValidValue(item, [
      "channelLevel",
      "shopLevel",
      "level",
      "dealerLevel",
    ]) : "";
    const channelType = this.showChannelInfo ? this._getFirstValidValue(item, [
      "channelType",
      "dealerType",
      "type",
    ]) : "";
    const productSeries = this.showProductInfo ? this._getFirstValidValue(item, [
      "productSeries",
      "productFilter",
      "productModels",
    ]) : "";
    const providedServices = this.showServiceInfo ? this._getFirstValidValue(item, [
      "providedServices",
      "services",
      "service",
    ]) : "";
    const productSeriesSection = this._renderFieldGroup(this.productSeriesText, productSeries, {
      maxTags: 8,
    });
    const servicesSection = this._renderFieldGroup(this.servicesText, providedServices);
    const contactSection = this._renderContactGroup(item);
    const cardSections = [
      this._renderMetaRow(channelType, channelLevel),
      productSeriesSection,
      servicesSection,
      contactSection,
    ].filter(Boolean).join(this._renderDivider());

    el.innerHTML = `
      <div class="custom-dealer-item-title flex justify-between items-start gap-4">
        <div class="custom-dealer-item-name">${name}</div>
        ${item.distanceInMeters != null ? this._renderDistance(item.distanceInMeters) : ""}
      </div>
      ${cardSections}
      ${this._renderActions(item)}
    `;
    return el;
  }

  _isValidValue(value) {
    return value != null && String(value).trim() !== "" && String(value).trim() !== "null";
  }

  _getBooleanAttribute(name, defaultValue = true) {
    if (!this.hasAttribute(name)) return defaultValue;
    return this.getAttribute(name) !== "false";
  }

  _getFirstValidValue(item, keys) {
    for (const key of keys) {
      if (this._isValidValue(item?.[key])) return item[key];
    }
    return "";
  }

  _escapeHTML(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  _splitFieldValues(value) {
    if (!this._isValidValue(value)) return [];
    return String(value)
      .split(";")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  _renderMetaRow(channelType, channelLevel) {
    const tags = [channelType, channelLevel]
      .filter((value) => this._isValidValue(value))
      .map(
        (value) =>
          `<span class="custom-dealer-meta-tag">${this._escapeHTML(value)}</span>`,
      )
      .join("");

    if (!tags) return "";
    return `<div class="custom-dealer-meta-row">${tags}</div>`;
  }

  _renderDivider() {
    return `<div class="custom-dealer-divider" aria-hidden="true"></div>`;
  }

  _renderFieldGroup(label, value, options = {}) {
    const tags = this._splitFieldValues(value);
    if (tags.length === 0) return "";
    const maxTags = Number(options.maxTags) || 0;
    const visibleTags = maxTags > 0 ? tags.slice(0, maxTags) : tags;
    const hiddenCount = maxTags > 0 ? Math.max(tags.length - maxTags, 0) : 0;

    return `
      <div class="custom-dealer-field-group">
        <div class="custom-dealer-field-label">${label}</div>
        <div class="custom-dealer-field-tags">
          ${visibleTags
            .map(
              (tag) =>
                `<span class="custom-dealer-field-tag">${this._escapeHTML(tag)}</span>`,
            )
            .join("")}
          ${hiddenCount > 0 ? `<span class="custom-dealer-field-tag custom-dealer-field-tag--more">+${hiddenCount}</span>` : ""}
        </div>
      </div>
    `;
  }

  _renderContactGroup(item) {
    const contacts = [
      item.address && item.address !== "null" ? this._renderAddress(item) : "",
      this.showBusinessHourInfo && item.businessHour && item.businessHour !== "null" ? this._renderBusinessHour(item.businessHour) : "",
      item.phone && item.phone !== "null" ? this._renderPhone(item.phone, item.name, item.dealerId) : "",
      item.email && item.email !== "null" ? this._renderEmail(item.email, item.name, item.dealerId) : "",
    ].join("");

    if (!contacts) return "";
    return `<div class="custom-dealer-item-contact flex flex-col gap-y-[10px] [&_svg]:mt-[1.2px]">${contacts}</div>`;
  }

  _renderActions(item) {
    const lat = Number(item.latitude);
    const lng = Number(item.longitude);

    return `
      <div class="custom-dealer-actions">
        <button
          type="button"
          class="custom-dealer-directions-btn"
          data-store-name="${this._escapeHTML(item.name)}"
          data-store-id="${this._escapeHTML(item.dealerId)}"
          data-lat="${Number.isFinite(lat) ? lat : ""}"
          data-lng="${Number.isFinite(lng) ? lng : ""}">
          ${this._escapeHTML(this.directionsText)}
        </button>
        <button type="button" class="custom-dealer-more-info-btn">
          ${this._escapeHTML(this.moreInformationText)}
        </button>
      </div>
    `;
  }

  _renderTags(productFilter) {
    if (!productFilter || productFilter === "null") return "";
    return `<div class="flex gap-[6px] flex-wrap">
      ${productFilter
        .split(";")
        .filter((tag) => tag.trim())
        .map(
          (tag) =>
            `<span class='px-[8px] py-[2px] rounded-[4px] bg-[#0000000A] text-[12px] font-[600] leading-[150%] tracking-[-0.12px] text-[#000000E0]'>${tag}</span>`,
        )
        .join("")}
    </div>`;
  }

  _renderPhone(phone, name, dealerId) {
    return `<a href="tel:${this._escapeHTML(phone)}" class="render-phone custom-dealer-item-info" data-store-name="${this._escapeHTML(name)}" data-store-id="${this._escapeHTML(dealerId)}">
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M0.566933 3.13968C1.52672 6.90979 4.49467 9.87774 8.26478 10.8375C9.69201 11.2009 10.9045 9.97276 10.9045 8.5V7.83333C10.9045 7.46514 10.6052 7.17017 10.2388 7.13385C9.61982 7.07249 9.02569 6.92646 8.46821 6.70749L7.45535 7.72034C5.80799 6.93199 4.47247 5.59647 3.68412 3.94911L4.69697 2.93626C4.478 2.37877 4.33197 1.78464 4.27061 1.16561C4.23429 0.799217 3.93932 0.5 3.57113 0.5H2.90446C1.4317 0.5 0.203588 1.71245 0.566933 3.13968Z" stroke="#707070" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${this._escapeHTML(phone)}</span>
    </a>`;
  }

  _renderBusinessHour(businessHour) {
    return `<div class="custom-dealer-business-hour custom-dealer-item-info">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="6" stroke="#707070" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.3335 5.33398V8.66732H10.6668" stroke="#707070" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${this._escapeHTML(businessHour)}</span>
    </div>`;
  }

  _renderEmail(email, name, dealerId) {
    return `<a href="mailto:${this._escapeHTML(email)}" class="render-email custom-dealer-item-info" data-store-name="${this._escapeHTML(name)}" data-store-id="${this._escapeHTML(dealerId)}">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2 3.33398H14V11.334C14 12.0704 13.403 12.6673 12.6667 12.6673H3.33333C2.59695 12.6673 2 12.0704 2 11.334V3.33398Z" stroke="#707070" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M2 3.33398L8 9.33398L14 3.33398" stroke="#707070" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${this._escapeHTML(email)}</span>
    </a>`;
  }

  _renderAddress(item) {
    const lat = Number(item.latitude);
    const lng = Number(item.longitude);
    return `<a target="_blank" href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}"
    class="render-address wtb-navigation custom-dealer-item-info"
    data-store-name="${this._escapeHTML(item.name)}"
    data-store-id="${this._escapeHTML(item.dealerId)}"
    data-lat="${Number.isFinite(lat) ? lat : ""}"
    data-lng="${Number.isFinite(lng) ? lng : ""}">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="8" y="7.33398" width="0.00666667" height="0.00666667" stroke="#707070" stroke-width="2" stroke-linejoin="round"/>
        <path d="M7.99984 14.6667L11.6665 11C13.6915 8.97496 13.6915 5.69171 11.6665 3.66667C9.64146 1.64162 6.35821 1.64162 4.33317 3.66667C2.30813 5.69171 2.30813 8.97496 4.33317 11L7.99984 14.6667Z" stroke="#707070" stroke-linejoin="round"/>
      </svg>
      <span>${this._escapeHTML(item.address)}</span>
    </a>`;
  }

  _renderWebsite(website, storeName, storeId) {
    const url = String(website).startsWith("http") ? website : `https://${website}`;
    return `<a href="${this._escapeHTML(url)}" target="_blank" data-store-name="${this._escapeHTML(storeName)}"
      data-store-id="${this._escapeHTML(storeId)}" class="render-website custom-dealer-item-info">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M6 11.5V12.1716C6 12.702 5.78929 13.2107 5.41421 13.5858L5 14H11L10.5858 13.5858C10.2107 13.2107 10 12.702 10 12.1716V11.5M14 3.5V10C14 10.8284 13.3284 11.5 12.5 11.5H3.5C2.67157 11.5 2 10.8284 2 10V3.5M14 3.5C14 2.67157 13.3284 2 12.5 2H3.5C2.67157 2 2 2.67157 2 3.5M14 3.5V8C14 8.82843 13.3284 9.5 12.5 9.5H3.5C2.67157 9.5 2 8.82843 2 8V3.5" stroke="#707070" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${this.visitWebsiteText}</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" style="width: 14px !important; height: 14px !important; margin-top: 3px;">
        <path d="M11.2002 3.49999C11.2002 3.1128 10.8874 2.79999 10.5002 2.79999H5.60023C5.21305 2.79999 4.90023 3.1128 4.90023 3.49999C4.90023 3.88718 5.21305 4.19999 5.60023 4.19999H8.8093L3.00586 10.0056C2.73242 10.279 2.73242 10.7231 3.00586 10.9966C3.2793 11.27 3.72336 11.27 3.9968 10.9966L9.80023 5.19093V8.39999C9.80023 8.78718 10.113 9.09999 10.5002 9.09999C10.8874 9.09999 11.2002 8.78718 11.2002 8.39999V3.49999Z" fill="#707070"/>
      </svg></a>`;
  }

  _renderDistance(distance) {
    let distanceStr = "";
    if (this.unit === " miles" && distance) {
      distanceStr = Math.round(distance) + this.unit;
    }
    if (this.unit === " km" && distance) {
      distanceStr = Math.round(distance / 1000) + this.unit;
    }
    return `<div class="flex items-center gap-1">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M13.5473 3.48747C13.6598 3.19247 13.5898 2.85747 13.3673 2.63497C13.1448 2.41247 12.8098 2.33997 12.5148 2.45497L2.11481 6.45497C1.79731 6.57497 1.59231 6.87997 1.59981 7.21747C1.60731 7.55497 1.82731 7.85247 2.14731 7.95747L6.56731 9.42998L8.03981 13.85C8.14731 14.17 8.44231 14.39 8.77981 14.3975C9.11731 14.405 9.42231 14.2 9.54481 13.885L13.5448 3.48497L13.5473 3.48747Z" fill="black" fill-opacity="0.88"/>
      </svg>
      <span class="text-[14px] font-[600] leading-[125%] tracking-[-0.14px] text-[#000000E0] whitespace-nowrap">${distanceStr}</span>
    </div>`;
  }

  _renderCornerMark(cornerMark) {
    return `<div class="flex items-center flex-wrap gap-[6px]">
      ${cornerMark
        .split(";")
        .filter((tag) => tag.trim())
        .map(
          (tag) =>
            `<div class='py-[2px] px-[8px] rounded-[4px] bg-[#FF5A0014] text-[12px] font-[600] leading-[150%] tracking-[-0.12px] text-[#FF5A00] w-fit'>${tag}</div>`,
        )
        .join("")}
    </div>`;
  }
  _pushGA4Event(eventName, storeName = "", storeId = "") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      store_name: storeName,
      store_id: storeId,
    });
  }

  _isWhereToBuyV4() {
    return !!this.closest(".where-to-buy-v4");
  }

  _getGA4Heading() {
    return (
      this.getAttribute("ga4-heading") ||
      this.closest(".where-to-buy-v4")?.dataset.ga4Heading ||
      ""
    );
  }

  _pushWTBV4GA4Event(eventName, parameters = {}) {
    if (!this._isWhereToBuyV4()) return;
    const eventParameters = {
      current_page: window.location.href,
      heading: this._getGA4Heading(),
      ...parameters,
    };

    Object.keys(eventParameters).forEach((key) => {
      if (eventParameters[key] == null) eventParameters[key] = "";
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: "ga4Event",
      event_name: eventName,
      event_parameters: eventParameters,
    });
  }

  _handleDelegatedClick(e) {
    const directionsButton = e.target.closest(".custom-dealer-directions-btn");
    if (directionsButton) {
      e.preventDefault();
      e.stopPropagation();
      const lat = directionsButton.dataset.lat || "";
      const lng = directionsButton.dataset.lng || "";
      if (!lat || !lng) return;
      this._pushGA4Event(
        "GA4-storelocator-navigation",
        directionsButton.dataset.storeName || "",
        directionsButton.dataset.storeId || "",
      );
      this._pushWTBV4GA4Event("map_card_directions_click", {
        title: directionsButton.dataset.storeName || "",
        button_name: directionsButton.textContent?.trim() || this.directionsText,
        subheading: "Bref",
      });
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
        "_blank",
      );
      return;
    }

    const moreInfoButton = e.target.closest(".custom-dealer-more-info-btn");
    if (moreInfoButton) {
      e.preventDefault();
      e.stopPropagation();
      const item = moreInfoButton.closest(".custom-dealer-item");
      if (item && this.contains(item)) this._handleItemClick(item, e);
      return;
    }

    const link = e.target.closest(".render-website, .render-address.wtb-navigation, .render-phone, .render-email");
    if (link) {
      const storeName = link.dataset.storeName || "";
      const storeId = link.dataset.storeId || "";
      if (link.classList.contains("render-address")) {
        e.preventDefault();
        const lat = link.dataset.lat || "";
        const lng = link.dataset.lng || "";
        if (!lat || !lng) return;
        this._pushGA4Event("GA4-storelocator-navigation", storeName, storeId);
        this._pushWTBV4GA4Event("map_card_address_click", {
          title: storeName,
          subheading: "Bref",
        });
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
          "_blank",
        );
      } else if (link.classList.contains("render-phone")) {
        this._pushGA4Event("GA4-storelocator-phone", storeName, storeId);
        this._pushWTBV4GA4Event("map_card_phone_click", {
          title: storeName,
          subheading: "Bref",
        });
      } else if (link.classList.contains("render-email")) {
        this._pushGA4Event("GA4-storelocator-email", storeName, storeId);
        this._pushWTBV4GA4Event("map_card_mail_click", {
          title: storeName,
          subheading: "Bref",
        });
      } else if (link.classList.contains("render-website")) {
        this._pushGA4Event("GA4-storelocator-visit-website", storeName, storeId);
        this._pushWTBV4GA4Event("map_card_website_click", {
          title: storeName,
          subheading: "Bref",
        });
      }
      e.stopPropagation();
      return;
    }

    if (e.target.closest("a, button")) {
      return;
    }

    const item = e.target.closest(".custom-dealer-item");
    if (!item || !this.contains(item)) return;

    this._handleItemClick(item, e);
  }

  _addClickHandlers() {
    if (!this._clickDelegated) this.connectedCallback();
  }

  _dispatchDealerSelected(dealerData, delay = 0) {
    window.clearTimeout(this._dealerSelectedTimer);

    const dispatch = () => {
      this._dealerSelectedTimer = null;
      if (!this.isConnected) return;
      this.dispatchEvent(
        new CustomEvent("dealer-selected", {
          detail: dealerData,
          bubbles: true,
        }),
      );
    };

    if (delay > 0) {
      this._dealerSelectedTimer = window.setTimeout(dispatch, delay);
    } else {
      dispatch();
    }
  }

  _isMobileWhereToBuyV4() {
    return window.innerWidth < 768 && !!this.closest(".where-to-buy-v4");
  }

  _prepareMobileDealerSelection() {
    if (!this._isMobileWhereToBuyV4()) return;
    if (window.WhereToBuyV4?.collapsePanelToHalfIfFull) {
      window.WhereToBuyV4.collapsePanelToHalfIfFull();
    }
  }
  
  _handleItemClick(item, e) {
    const dealerId = item.dataset.dealerid;
    const dealerData =
      item._dealerData ||
      this.items.find((d) => d.dealerId == dealerId) ||
      (this.pinnedDealerId === String(dealerId) ? this.pinnedDealer : null);
    const isMobileWhereToBuyV4 = this._isMobileWhereToBuyV4();
    const detailOpenDelay = isMobileWhereToBuyV4
      ? this.MOBILE_DETAIL_OPEN_DELAY_MS
      : 0;

    this._pushWTBV4GA4Event("map_card_click", {
      title: item.dataset.name || dealerData?.name || "",
      subheading: "Bref",
    });

    this._prepareMobileDealerSelection();
    if (!item.classList.contains("custom-dealer-item--pinned") && this.pinnedDealerId) {
      this._clearPinnedDealer();
    }

    // 如果当前点击的就是已经激活的，地图状态保持不变，只打开详情。
    if (item.classList.contains("active")) {
      this._dispatchDealerSelected(dealerData, detailOpenDelay);
      return;
    }
    this._setActiveDealer(item.dataset.dealerid);

    map.setZoom(13);
    const targetCenter = {
      lat: Number(item.dataset.lat),
      lng: Number(item.dataset.long),
    };
    map.setCenter(
      window.innerWidth < 768 && window.WhereToBuyV4?.getAdjustedCenter
        ? window.WhereToBuyV4.getAdjustedCenter(targetCenter, map)
        : targetCenter,
    );
    const marker = markers.find(
      (m) => (m.dealerid || (m.dataset && m.dataset.dealerid)) == item.dataset.dealerid,
    );
    if (marker) {
      if (typeof window.setSelectedMarker === "function") {
        window.setSelectedMarker(marker);
      }
      commonInfoWindow.setContent(
        this._renderInfoWindow({
          name: item.dataset.name,
          latitude: Number(item.dataset.lat),
          longitude: Number(item.dataset.long),
          distanceInMeters: item.dataset.distanceinmeters,
          dealerId: item.dataset.dealerid,
        }),
      );
      if (currentInfoWindow) currentInfoWindow.close();
      commonInfoWindow.open(map, marker);
      currentInfoWindow = commonInfoWindow;
      this._pushGA4Event(
        "GA4-storelocator-storeselect",
        item.dataset.name,
        item.dataset.dealerid,
      );
      // infoWindow 动态内容加载完成后绑定事件
      google.maps.event.addListenerOnce(
        commonInfoWindow,
        "domready",
        () => {
          this._addInfoWindowNavigationClickHandlers();
        },
      );
    }

    this._dispatchDealerSelected(dealerData, detailOpenDelay);
  }

  _addInfoWindowNavigationClickHandlers() {
    // 谷歌地图 infoWindow 容器
    const container = document.querySelector(".gm-style-iw");
    if (!container) return;

    container.querySelectorAll(".render-info.wtb-navigation").forEach((el) => {
      el.onclick = null; // 防止重复绑定
      el.onclick = (e) => {
        e.stopPropagation();
        const storeName = el.dataset.storeName || "";
        const storeId = el.dataset.storeId || "";
        const lat = el.dataset.lat || "";
        const lng = el.dataset.lng || "";
        this._pushGA4Event("GA4-storelocator-navigation", storeName, storeId);
        this._pushWTBV4GA4Event("map_directions_click", {
          button_name: el.textContent?.trim() || this.directionsText,
        });
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
          "_blank",
        );
      };
    });
  }

  _renderInfoWindow(location) {
    let distance = "";
    if (this.unit === " miles" && location.distanceInMeters) {
      distance = Math.round(location.distanceInMeters) + this.unit;
    }
    if (this.unit === " km" && location.distanceInMeters) {
      distance = Math.round(location.distanceInMeters / 1000) + this.unit;
    }
    return `
      <div class="flex items-center gap-4"> 
        <div class="text-[16px] font-semibold leading-[125%] tracking-[-0.16px] text-[rgba(0,0,0,0.88)]">${location.name}</div>
        <div class="wtb-navigation render-info flex flex-col items-center gap-1 cursor-pointer px-[10px] py-[8px] rounded-[8px] bg-[rgba(255,90,0,0.08)]"
        data-store-name="${location.name}"
        data-store-id="${location.dealerId || ''}"
        data-lat="${location.latitude}"
        data-lng="${location.longitude}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M15.241 3.92343C15.3675 3.59156 15.2888 3.21468 15.0385 2.96437C14.7882 2.71406 14.4113 2.63249 14.0794 2.76187L2.3794 7.26187C2.02222 7.39687 1.79159 7.73999 1.80003 8.11968C1.80847 8.49937 2.05597 8.83406 2.41597 8.95218L7.38847 10.6087L9.04503 15.5812C9.16597 15.9412 9.49784 16.1887 9.87753 16.1972C10.2572 16.2056 10.6003 15.975 10.7382 15.6206L15.2382 3.92062L15.241 3.92343Z" fill="#FF5A00"/>
          </svg>
          <span class="text-[12px] font-semibold leading-[150%] tracking-[-0.12px] text-[#FF5A00] text-center whitespace-nowrap">${distance}</span>
        </div>
      </div>
    `;
  }
}

customElements.define("custom-dealer-list-v3", CustomDealerListV3);

// ============================================================================
// CustomDealerDetail - 经销商详情页组件
// ============================================================================
// 功能：显示选中经销商的详细信息，包括地址、电话、邮箱、网站等
// 依赖：监听 CustomDealerListV3 触发的 'dealer-selected' 事件
// ============================================================================

class CustomDealerDetail extends HTMLElement {
  constructor() {
    super();
    this.els = {};
  }

  connectedCallback() {
    this.unit = this.getAttribute("unit") || " miles";
    this.showProductInfo = this._getBooleanAttribute("show-product-info", true);
    this.showServiceInfo = this._getBooleanAttribute("show-service-info", true);
    this.showBusinessHourInfo = this._getBooleanAttribute("show-business-hour-info", true);
    this.currentDealer = null;
    this.initElements();
    this.bindEvents();
  }

  initElements() {
    this.els = {
      view: this.querySelector("#dealer-detail-view"),
      name: this.querySelector("#detail-name"),
      address: this.querySelector("#detail-address"),
      addressText: this.querySelector("#detail-address-text"),
      businessHour: this.querySelector("#detail-business-hour"),
      businessHourText: this.querySelector("#detail-business-hour-text"),
      distance: this.querySelector("#detail-distance"),
      distanceText: this.querySelector("#detail-distance-text"),
      phone: this.querySelector("#detail-phone"),
      phoneText: this.querySelector("#detail-phone-text"),
      email: this.querySelector("#detail-email"),
      emailText: this.querySelector("#detail-email-text"),
      website: this.querySelector("#detail-website"),
      modelsList: this.querySelector("#detail-models-list"),
      modelsSection: this.querySelector("#detail-models-section"),
      servicesList: this.querySelector("#detail-services-list"),
      servicesSection: this.querySelector("#detail-services-section"),
      backBtn: this.querySelector("#detail-back-btn"),
      // Buttons inside the snippet
      testDriveBtn: this.querySelector("#detail-test-drive-btn"),
      serviceBtn: this.querySelector("#detail-service-btn"),
      directionsBtn: this.querySelector("#detail-directions-btn"),
      feedbackBtn: this.querySelector("#detail-feedback-btn"),
    };
  }

  isV4Detail() {
    return !!this.closest(".where-to-buy-v4");
  }

  _getBooleanAttribute(name, defaultValue = true) {
    if (!this.hasAttribute(name)) return defaultValue;
    return this.getAttribute(name) !== "false";
  }

  bindEvents() {
    // Listen to event from list
    // We assume the list has ID 'custom-dealer-list' as per the existing code
    const list = document.getElementById("custom-dealer-list");
    if (list) {
      // Remove existing listener if any (though typically this is a fresh init)
      list.removeEventListener("dealer-selected", this.onDealerSelected);
      list.addEventListener(
        "dealer-selected",
        this.onDealerSelected.bind(this),
      );
    } else {
      // Retry if list not yet available
      setTimeout(() => this.bindEvents(), 100);
    }

    if (this.els.backBtn) {
      this.els.backBtn.addEventListener("click", this.close.bind(this));
    }

    if (this.els.testDriveBtn) {
      this.els.testDriveBtn.addEventListener(
        "click",
        this.onTestDriveClick.bind(this),
      );
    }

    if (this.els.serviceBtn) {
      this.els.serviceBtn.addEventListener(
        "click",
        this.onServiceClick.bind(this),
      );
    }

    if (this.els.directionsBtn) {
      this.els.directionsBtn.addEventListener(
        "click",
        this.onDirectionsClick.bind(this),
      );
    }

    if (this.els.feedbackBtn) {
      this.els.feedbackBtn.addEventListener(
        "click",
        this.onFeedbackClick.bind(this),
      );
    }

    // Detail 页面中的链接点击：真实跳转时做埋点
    this._bindDetailLinkEvents();
  }

  _bindAddressNavigation() {
    if (!this.els.address) return;
    
    // 先移除旧的监听器（如果存在）
    if (this._addressNavigationHandler) {
      this.els.address.removeEventListener("click", this._addressNavigationHandler);
    }
    
    // 创建新的处理函数
    this._addressNavigationHandler = () => {
      const storeName = this.els.address.dataset.storeName || "";
      const storeId = this.els.address.dataset.storeId || "";
      const lat = this.els.address.dataset.lat || "";
      const lng = this.els.address.dataset.lng || "";
      
      if (lat && lng) {
        this._pushGA4Event("GA4-storelocator-navigation", storeName, storeId);
        this._pushWTBV4GA4Event("map_card_address_click", {
          title: storeName,
          subheading: "Detail",
        });
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
          "_blank"
        );
      }
    };
    
    // 绑定新的事件监听器
    this.els.address.addEventListener("click", this._addressNavigationHandler);
  }

  _bindDetailLinkEvents() {
    if (this.els.phone) {
      this.els.phone.addEventListener("click", () => {
        const storeName = this.currentDealer?.name || "";
        const storeId = this.currentDealer?.dealerId || "";
        this._pushGA4Event("GA4-storelocator-phone", storeName, storeId);
        this._pushWTBV4GA4Event("map_card_phone_click", {
          title: storeName,
          subheading: "Detail",
        });
      });
    }

    if (this.els.email) {
      this.els.email.addEventListener("click", () => {
        const storeName = this.currentDealer?.name || "";
        const storeId = this.currentDealer?.dealerId || "";
        this._pushGA4Event("GA4-storelocator-email", storeName, storeId);
        this._pushWTBV4GA4Event("map_card_mail_click", {
          title: storeName,
          subheading: "Detail",
        });
      });
    }

    if (this.els.website) {
      this.els.website.addEventListener("click", () => {
        const storeName = this.currentDealer?.name || "";
        const storeId = this.currentDealer?.dealerId || "";
        this._pushGA4Event("GA4-storelocator-visit-website", storeName, storeId);
        this._pushWTBV4GA4Event("map_card_website_click", {
          title: storeName,
          subheading: "Detail",
        });
      });
    }
  }

  _pushGA4Event(eventName, storeName = "", storeId = "") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      store_name: storeName,
      store_id: storeId,
    });
  }

  _isWhereToBuyV4Detail() {
    return !!this.closest(".where-to-buy-v4");
  }

  _getGA4Heading() {
    return (
      this.getAttribute("ga4-heading") ||
      this.closest(".where-to-buy-v4")?.dataset.ga4Heading ||
      ""
    );
  }

  _pushWTBV4GA4Event(eventName, parameters = {}) {
    if (!this._isWhereToBuyV4Detail()) return;
    const eventParameters = {
      current_page: window.location.href,
      heading: this._getGA4Heading(),
      ...parameters,
    };

    Object.keys(eventParameters).forEach((key) => {
      if (eventParameters[key] == null) eventParameters[key] = "";
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: "ga4Event",
      event_name: eventName,
      event_parameters: eventParameters,
    });
  }

  _bindDistanceNavigation() {
    if (!this.els.distance) return;
    
    // 先移除旧的监听器（如果存在）
    if (this._distanceNavigationHandler) {
      this.els.distance.removeEventListener("click", this._distanceNavigationHandler);
    }
    
    // 创建新的处理函数
    this._distanceNavigationHandler = (e) => {
      e.stopPropagation();
      
      const storeName = this.els.distance.dataset.storeName || "";
      const storeId = this.els.distance.dataset.storeId || "";
      const lat = this.els.distance.dataset.lat || "";
      const lng = this.els.distance.dataset.lng || "";
      
      if (!lat || !lng) return;
      
      // 埋点
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "GA4-storelocator-navigation",
        store_name: storeName,
        store_id: storeId
      });
      this._pushWTBV4GA4Event("map_card_directions_click", {
        title: storeName,
        button_name: this.els.distance.textContent?.trim() || this.directionsText,
        subheading: "Detail",
      });
      
      // 打开 Google Maps 导航
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
        "_blank"
      );
    };
    
    // 绑定新的事件监听器
    this.els.distance.addEventListener("click", this._distanceNavigationHandler);
  }

  onTestDriveClick(e) {
    e.preventDefault();
    const modal = document.querySelector("custom-test-drive-modal");
    if (modal) {
      modal.open(this.currentDealer);
    }
  }

  onServiceClick(e) {
    e.preventDefault();
    const modal = document.querySelector("custom-request-service-modal");
    if (modal) {
      modal.open(this.currentDealer);
    }
  }

  onDirectionsClick(e) {
    e.preventDefault();
    this.openDirections(this.currentDealer);
  }

  onFeedbackClick(e) {
    e.preventDefault();
    if (!this.currentDealer) return;
    this._pushWTBV4GA4Event("map_card_error_click", {
      title: this.currentDealer.name || "",
      button_name: this.els.feedbackBtn?.textContent?.trim() || "",
      subheading: "Detail",
    });
    this.dispatchEvent(
      new CustomEvent("where-to-buy-feedback-open", {
        detail: this.currentDealer,
        bubbles: true,
      }),
    );
  }

  openDirections(dealer) {
    if (!dealer?.latitude || !dealer?.longitude) return;
    const storeName = dealer.name || "";
    const storeId = dealer.dealerId || "";
    this._pushGA4Event("GA4-storelocator-navigation", storeName, storeId);
    this._pushWTBV4GA4Event("map_card_directions_click", {
      title: storeName,
      button_name: this.els.directionsBtn?.textContent?.trim() || this.directionsText,
      subheading: "Detail",
    });
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${dealer.latitude},${dealer.longitude}`,
      "_blank"
    );
  }

  getConsultationText(data) {
    const keys = [
      "consultation",
      "consultationService",
      "consultationContent",
      "consultationDesc",
      "consultationDescription",
    ];
    for (const key of keys) {
      const value = data?.[key];
      if (value && value !== "null") return String(value).trim();
    }
    return "";
  }

  escapeHTML(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  renderV4Services(data) {
    if (!this.els.servicesList || !this.els.servicesSection) return;

    const consultationText = this.getConsultationText(data);
    const providedServices = String(data?.providedServices || "")
      .split(";")
      .map((service) => service.trim())
      .filter((service) => service && service !== "null");
    const i18n = window.wtbV4I18n || {};
    const installText =
      i18n.repairDescription ||
      "Quick response, precise repairs, genuine parts - extend your equipment's life.";
    const consultationIconSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <path d="M20 5.75C27.8701 5.75 34.25 12.1299 34.25 20C34.25 27.8701 27.8701 34.25 20 34.25C17.6446 34.25 15.4222 33.6789 13.4648 32.667L5.75 34.25L7.33301 26.5352C6.32115 24.5778 5.75 22.3554 5.75 20C5.75 12.1299 12.1299 5.75 20 5.75ZM13 18.0156C11.8954 18.0156 11 18.9111 11 20.0156C11 21.1202 11.8954 22.0156 13 22.0156C14.1046 22.0156 15 21.1202 15 20.0156C15 18.9111 14.1046 18.0156 13 18.0156ZM20 18.0156C18.8954 18.0156 18 18.9111 18 20.0156C18 21.1202 18.8954 22.0156 20 22.0156C21.1046 22.0156 22 21.1202 22 20.0156C22 18.9111 21.1046 18.0156 20 18.0156ZM27 18.0156C25.8954 18.0156 25 18.9111 25 20.0156C25 21.1202 25.8954 22.0156 27 22.0156C28.1046 22.0156 29 21.1202 29 20.0156C29 18.9111 28.1046 18.0156 27 18.0156Z" fill="url(#wtbV4ServiceIconGradient)"/>
        <defs>
          <linearGradient id="wtbV4ServiceIconGradient" x1="13.346" y1="5.75" x2="13.346" y2="34.25" gradientUnits="userSpaceOnUse">
            <stop stop-color="#FF7F39"/>
            <stop offset="1" stop-color="#FF5A00"/>
          </linearGradient>
        </defs>
      </svg>
    `;
    const installIconSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
        <path d="M15 0C23.2843 0 30 6.71573 30 15C30 23.2843 23.2843 30 15 30C6.71573 30 0 23.2843 0 15C0 6.71573 6.71573 0 15 0ZM17.4326 8.08301C15.6848 7.72699 13.8307 8.49937 12.8896 10.1289C12.1157 11.4698 12.152 13.0571 12.8477 14.3213L10.6123 18.1934C9.99168 19.269 10.3602 20.6445 11.4355 21.2656C12.5112 21.8866 13.8866 21.5178 14.5078 20.4424L16.7725 16.5205C18.1765 16.4565 19.5193 15.7009 20.2744 14.3936C21.2149 12.7644 20.9581 10.7731 19.7773 9.4375L18.8408 11.0586C18.4669 11.7059 17.6396 11.9274 16.9922 11.5537C16.3448 11.1799 16.1225 10.3526 16.4961 9.70508L17.4326 8.08301Z" fill="#231815"/>
        <path d="M15 0C23.2843 0 30 6.71573 30 15C30 23.2843 23.2843 30 15 30C6.71573 30 0 23.2843 0 15C0 6.71573 6.71573 0 15 0ZM17.4326 8.08301C15.6848 7.72699 13.8307 8.49937 12.8896 10.1289C12.1157 11.4698 12.152 13.0571 12.8477 14.3213L10.6123 18.1934C9.99168 19.269 10.3602 20.6445 11.4355 21.2656C12.5112 21.8866 13.8866 21.5178 14.5078 20.4424L16.7725 16.5205C18.1765 16.4565 19.5193 15.7009 20.2744 14.3936C21.2149 12.7644 20.9581 10.7731 19.7773 9.4375L18.8408 11.0586C18.4669 11.7059 17.6396 11.9274 16.9922 11.5537C16.3448 11.1799 16.1225 10.3526 16.4961 9.70508L17.4326 8.08301Z" fill="url(#wtbV4InstallIconGradient)"/>
        <defs>
          <linearGradient id="wtbV4InstallIconGradient" x1="7.99582" y1="0" x2="7.99582" y2="30" gradientUnits="userSpaceOnUse">
            <stop stop-color="#FF7F39"/>
            <stop offset="1" stop-color="#FF5A00"/>
          </linearGradient>
        </defs>
      </svg>
    `;

    const services = [
      {
        title: i18n.presalesConsultation || "Pre-sales Consultation",
        text:
          consultationText ||
          i18n.presalesConsultationDescription ||
          "Free professional advice - from selection and operation to maintenance.",
        iconSvg: consultationIconSvg,
      },
      { title: i18n.repair || "Repair", text: installText, iconSvg: installIconSvg },
    ];

    this.els.servicesList.innerHTML = services
      .map(
        (service, index) => `
          <div class="wtb-v4-service-item">
            <div class="wtb-v4-service-icon">${service.iconSvg}</div>
            <div class="wtb-v4-service-copy">
              <div>${this.escapeHTML(providedServices[index] || service.title)}</div>
              <p>${this.escapeHTML(service.text)}</p>
            </div>
          </div>
        `,
      )
      .join("");
    this.els.servicesSection.style.display = "block";
  }

  onDealerSelected(e) {
    const data = e.detail;
    if (!data) return;
    this.currentDealer = data;
    this.render(data);
    // 移动端：延迟打开，确保所有内容已渲染完成，避免高度拉伸
    if (window.innerWidth < 768) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.open();
        });
      });
    } else {
      this.open();
    }
  }

  render(data) {
    if (!this.els.view) return;

    // Name
    if (this.els.name) this.els.name.textContent = data.name;

    // Address
    if (this.els.address) {
      if (data.address && data.address !== "null") {
        if (this.els.addressText) this.els.addressText.textContent = data.address;
        // 设置导航相关的 data 属性
        if (data.latitude && data.longitude) {
          this.els.address.dataset.storeName = data.name || "";
          this.els.address.dataset.storeId = data.dealerId || "";
          this.els.address.dataset.lat = data.latitude;
          this.els.address.dataset.lng = data.longitude;
        }
        this.els.address.style.display = "flex";
        // 绑定地址导航点击事件
        this._bindAddressNavigation();
      } else {
        this.els.address.style.display = "none";
      }
    }

    // Distance
    let distanceStr = "";
    if (data.distanceInMeters) {
      if (this.unit.includes("km")) {
        distanceStr = Math.round(data.distanceInMeters / 1000) + this.unit;
      } else {
        distanceStr = Math.round(data.distanceInMeters) + this.unit;
      }
    }

    if (this.els.distance) {
      if (distanceStr) {
        if (this.els.distanceText)
          this.els.distanceText.textContent = distanceStr;
        this.els.distance.style.display = "flex";
        // 设置导航相关的 data 属性
        if (data.latitude && data.longitude) {
          this.els.distance.dataset.storeName = data.name || "";
          this.els.distance.dataset.storeId = data.dealerId || "";
          this.els.distance.dataset.lat = data.latitude;
          this.els.distance.dataset.lng = data.longitude;
        }
        // 绑定导航点击事件
        this._bindDistanceNavigation();
      } else {
        this.els.distance.style.display = "none";
      }
    }

    // Business Hour
    if (this.els.businessHour) {
      if (this.showBusinessHourInfo && data.businessHour && data.businessHour !== "null") {
        if (this.els.businessHourText)
          this.els.businessHourText.textContent = data.businessHour;
        this.els.businessHour.style.display = "flex";
      } else {
        this.els.businessHour.style.display = "none";
      }
    }

    // Phone
    if (this.els.phone) {
      if (data.phone && data.phone !== "null") {
        if (this.els.phoneText) this.els.phoneText.textContent = data.phone;
        this.els.phone.href = `tel:${data.phone}`;
        this.els.phone.style.display = "flex";
      } else {
        this.els.phone.style.display = "none";
      }
    }

    // Email
    if (this.els.email) {
      if (data.email && data.email !== "null") {
        if (this.els.emailText) this.els.emailText.textContent = data.email;
        this.els.email.href = `mailto:${data.email}`;
        this.els.email.style.display = "flex";
      } else {
        this.els.email.style.display = "none";
      }
    }

    // Website
    if (this.els.website) {
      if (data.website && data.website !== "null") {
        this.els.website.href = data.website.startsWith("http")
          ? data.website
          : `https://${data.website}`;
        this.els.website.style.display = "flex";
      } else {
        this.els.website.style.display = "none";
      }
    }

    // Models (Tags)
    if (this.els.modelsList) {
      this.els.modelsList.innerHTML = "";
      if (this.showProductInfo && data.productModels && data.productModels !== "null") {
        const models = data.productModels
          .split(";")
          .map((s) => s.trim())
          .filter((s) => s);
        models.forEach((m) => {
          const tag = document.createElement("span");
          tag.className =
            "bg-[#0000000A] text-[#000000E0] px-2 rounded h-6 flex items-center";
          tag.textContent = m;
          this.els.modelsList.appendChild(tag);
        });
        // 移动端：先渲染内容但保持隐藏，在 open() 时统一显示
        // PC端立即显示
        if (window.innerWidth >= 768) {
          this.els.modelsSection.style.display = "block";
        }
        // 移动端保持 hidden，在 open() 时统一显示
      } else {
        this.els.modelsSection.style.display = "none";
      }
    }

    // Services (Tags)
    if (this.els.servicesList) {
      this.els.servicesList.innerHTML = "";
      if (!this.showServiceInfo) {
        this.els.servicesSection.style.display = "none";
        return;
      }
      if (this.isV4Detail()) {
        this.renderV4Services(data);
        return;
      }
      if (data.providedServices && data.providedServices !== "null") {
        const services = data.providedServices
          .split(";")
          .filter((tag) => tag.trim())
          .map((s) => s.trim());
        services.forEach((s) => {
          const tag = document.createElement("span");
          tag.className =
            "bg-[#0000000A] text-[#000000E0] px-2 rounded h-6 flex items-center";
          tag.textContent = s;
          this.els.servicesList.appendChild(tag);
        });
        // 移动端：先渲染内容但保持隐藏，在 open() 时统一显示
        // PC端立即显示
        if (window.innerWidth >= 768) {
          this.els.servicesSection.style.display = "block";
        }
        // 移动端保持 hidden，在 open() 时统一显示
      } else {
        this.els.servicesSection.style.display = "none";
      }
    }
  }

  open() {
    // 移动端：在打开前先显示所有已渲染的内容（models 和 services）
    if (window.innerWidth < 768) {
      if (this.els.modelsSection && this.els.modelsList.children.length > 0) {
        this.els.modelsSection.style.display = "block";
      }
      if (this.els.servicesSection && this.els.servicesList.children.length > 0) {
        this.els.servicesSection.style.display = "block";
      }
    }
    
    if (this.els.view) this.els.view.classList.add("active");
    const dealerContent = this.closest(".dealer-content");
    if (dealerContent) {
      dealerContent.classList.add("detail-mode");
    }

    // Mobile Bottom Sheet Handling（触发事件，由 liquid 文件中的监听器处理展开到最高）
    if (window.innerWidth < 768) {
      document.dispatchEvent(new CustomEvent("where-to-buy-detail-opened"));
    }
  }

  close() {
    if (this.els.view) this.els.view.classList.remove("active");

    const dealerContent = this.closest(".dealer-content");
    if (dealerContent) {
      dealerContent.classList.remove("detail-mode");
    }

    // Mobile Bottom Sheet Handling - 触发事件，由 liquid 文件中的监听器处理恢复原状
    if (window.innerWidth < 768) {
      document.dispatchEvent(new CustomEvent("where-to-buy-detail-closed"));
    }
  }
}

customElements.define("custom-dealer-detail", CustomDealerDetail);
